/**
 * @fileoverview Hans lesson recording page: select, record, review, saved
 * (iOS review B6).
 *
 * 1. **select** — who you are teaching, as one card, then the Record button.
 *    Today's sessions and everyone else live in the picker the card opens;
 *    a class session's register is a dialog off its attendance row.
 * 2. **recording** — the capture, with pause, resume and stop.
 * 3. **review** — listen, name the lesson, choose the instrument when there
 *    is more than one, Save lesson.
 * 4. **uploading** — upload in progress, then the lesson page.
 * 5. **saved** — a save that could not finish: the take is in this browser,
 *    with the one reason and the one fix that applies.
 *
 * Minutes are never sold here: one quiet line at the foot says what is left
 * and points at Plan & usage; out of minutes opens a dialog that does the
 * same. Consent is fixed on the student's page, not here.
 */

import { useState, useEffect, useRef, useMemo, useCallback, useContext } from "react";
import { useParams, useNavigate, useMatch, useSearchParams, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mic, Square, Pause, Play, ChevronLeft, ChevronRight,
  Users, Upload, Check, Loader2, Music, Pencil, UserPlus, User, Save, Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { getErrorMessage } from "@/lib/api";
import { pickAudioMimeType, isIOSBrowser, resolveAudioUploadType, readAudioDuration } from "@/lib/recording";
import { consentStateLabel } from "@/lib/consent";
import { useAiDisclosureGate } from "@/components/AiDisclosureDialog";
import { uploadQueue } from "@/lib/uploadQueue";
import { performUpload } from "@/lib/recordingUpload";
import { blockedReason, savedFix, savedReasonLine, clipLength } from "@/lib/savedRecordings";
import { recordingUsageLine } from "@/lib/recordingUsage";
import { toast } from "sonner";
import useUsage from "@/hooks/useUsage";
import { SCHEDULING_ENABLED, SCHEDULER_VIEW_ENABLED, subscriptionsEnabled, recordingToolsEnabled } from "@/lib/featureFlags";
import RecordingTools from "@/components/studio/RecordingTools";
import { RecordingPickerDialog } from "@/components/RecordingPickerDialog";
import { RecordingAttendanceDialog, attendanceSummary } from "@/components/RecordingAttendanceDialog";
import { OutOfMinutesDialog } from "@/components/OutOfMinutesDialog";
import { AuthContext } from "@/App";
import { formatTime12h, weekdayName, recordHrefForOccurrence } from "@/lib/schedule";
import { selectionFromOccurrence, selectionFromParams, selectionParams,
  sameSelection, teachingToday, verifySelection } from "@/lib/schedulingView";

/** The first visit's one line under Record; the localStorage flag that retires it. */
const EXPLAINER_SEEN_KEY = "hans_cockpit_explainer_seen";
export const FIRST_VISIT_LINE = "Hans listens and drafts the notes. Audio stays in this browser until you save.";

// What a take the upload queue would not hold reads on review: it is still
// on this screen to send again, never "saved" for the Saved recordings page,
// which lists queued takes only.
const STILL_HERE = "Your recording is still here, so try again.";

const studentName = (s) => `${s?.first_name || ""} ${s?.last_name || ""}`.trim();
const studentInitial = (s) => (studentName(s) || "?").charAt(0).toUpperCase();

/**
 * Animated sound wave visualization component.
 * @param {object} props
 * @param {boolean} props.active - Whether the animation should play.
 * @param {number} [props.count=32] - Number of wave bars to render.
 */
const SoundWave = ({ active, count = 32 }) => {
  const heights = useMemo(
    () => Array.from({ length: count }, () => Math.random() * 50 + 15),
    [count]
  );
  return (
    <div className="flex items-center justify-center gap-[2px] h-16 md:h-24">
      {heights.map((h, i) => (
        <motion.div
          key={i}
          className="w-1 bg-[var(--accent-yellow)] rounded-full"
          animate={active ? {
            height: [8, h, 8],
          } : { height: 8 }}
          transition={{
            duration: 0.6,
            repeat: active ? Infinity : 0,
            delay: i * 0.02,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
};

/** The small uppercase label above a block. */
const Kicker = ({ children, className = "" }) => (
  <p className={`text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--text-tertiary)] ${className}`}>{children}</p>
);

/**
 * Hans AI recording page component.
 * Guides the instructor through choosing who they teach, recording a lesson,
 * reviewing it and saving it for AI transcription and analysis. Supports
 * pre-selecting a student or a class via the route.
 * @returns {React.ReactElement}
 */
const HansRecording = () => {
  const { studentId: preSelectedStudentId } = useParams();
  const classMatch = useMatch("/hans/class/:classId");
  const classId = classMatch?.params?.classId || null;
  const isClassMode = !!classId;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stage, setStage] = useState("select");
  // Scheduling pre-link (Spec 04): the occurrence this recording fulfills —
  // {slotId, date} — stamped onto start-upload as scheduled_slot_id +
  // scheduled_date. Seeded from ?slotId&date deep links (Dashboard Today
  // card / SchedulePage) or by picking one of today's sessions in the picker.
  const [scheduledOccurrence, setScheduledOccurrence] = useState(() => {
    return SCHEDULING_ENABLED ? selectionFromParams(searchParams) : null;
  });
  const [selectionError, setSelectionError] = useState(null);
  const [selectionChecking, setSelectionChecking] = useState(!!scheduledOccurrence?.appointmentId);
  const [scheduledToday, setScheduledToday] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedInstrument, setSelectedInstrument] = useState("auto");
  // Re-record mode: opened from LessonSummary with ?rerecord=<lessonId> (+ the
  // original title). The new capture REPLACES the existing lesson's audio and
  // AI summary in place — start-upload is swapped for the lesson's
  // rerecord-upload endpoint, and confirm/navigate reuse the same lesson id.
  const rerecordLessonId = useMemo(() => searchParams.get("rerecord") || null, [searchParams]);
  const [lessonTitle, setLessonTitle] = useState(() => searchParams.get("title") || "");
  const [cls, setCls] = useState(null);
  const [classMembers, setClassMembers] = useState([]);

  // Practice-tools panel (metronome/tuner) hosted inside this screen. It used
  // to be two footer links that navigate()'d to /metronome and /tuner, which
  // unmounts this page — silently ending the recording mid-lesson.
  const [toolsOpen, setToolsOpen] = useState(false);
  const [toolsTab, setToolsTab] = useState("metronome");
  // The live capture, mirrored into state so the tuner can analyse it instead
  // of opening a second microphone (streamRef stays the teardown owner).
  const [captureStream, setCaptureStream] = useState(null);

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  // A validated audio file picked for upload instead of recorded live:
  // {file, duration, contentType, name}, awaiting the Save confirmation.
  const [pickedUpload, setPickedUpload] = useState(null);
  const fileInputRef = useRef(null);

  // The select stage's dialogs.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [attendanceOpen, setAttendanceOpen] = useState(false);
  // The out-of-minutes dialog, or null: `{ orgPool, saved }`.
  const [minutesDialog, setMinutesDialog] = useState(null);
  // The take on the saved stage: the queue entry and why it could not finish.
  const [savedTake, setSavedTake] = useState(null);
  const [consentSending, setConsentSending] = useState(false);
  // A take the upload queue refused, back on its review to be sent again:
  // its button reads "Try again" and the reason sits under it.
  const [takeKept, setTakeKept] = useState(false);
  const [keptReason, setKeptReason] = useState(null);
  // The first visit's line under Record: shown until the first Record click.
  const [explainerSeen, setExplainerSeen] = useState(() => {
    try {
      return !!localStorage.getItem(EXPLAINER_SEEN_KEY);
    } catch {
      return true;
    }
  });

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const mimeTypeRef = useRef("audio/webm");
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const stopRecordingRef = useRef(null);
  // Wall-clock duration tracking. Browsers throttle setInterval in hidden
  // tabs, so tick-counting undercounts long recordings — and duration_seconds
  // drives the usage quota and the 120-minute cap. Instead we timestamp each
  // active (non-paused) segment and derive the duration from Date.now(); the
  // interval below only refreshes the display.
  const segmentStartRef = useRef(null); // ms timestamp of the active segment, null while paused/stopped
  const accumulatedMsRef = useRef(0); // ms recorded in completed segments
  const navigateTimeoutRef = useRef(null);
  const wakeLockRef = useRef(null); // navigator.wakeLock sentinel held while recording
  // iOS-interruption bookkeeping (see the visibility effect + startRecording):
  const captureTrackRef = useRef(null); // the live mic MediaStreamTrack
  const recordingActiveRef = useRef(false); // true from start until OUR stop — distinguishes an external capture death
  const hiddenAtRef = useRef(null); // ms timestamp when the tab hid mid-recording
  // Crash-recovery persistence of the in-progress capture:
  const recoveryIdRef = useRef(null); // uploadQueue id of this capture's recovery entry
  const lastFlushSecondsRef = useRef(0);
  const flushPromiseRef = useRef(null); // in-flight flush write, awaited before the final upload enqueue
  const flushRecoveryRef = useRef(null);
  const buildUploadEntryRef = useRef(null);

  // iOS suspends the page — and the mic — on screen lock/app switch, and no
  // web page can prevent it; the recording stage warns upfront on iOS.
  const iosBrowser = isIOSBrowser();

  // Server-resolved paywall state: with subscriptions off the backend skips
  // the monthly quota entirely, so there is no ceiling to show.
  const { user, setUser, refreshUser } = useContext(AuthContext);
  const subsEnabled = subscriptionsEnabled(user);
  // Third-party AI disclosure (contract C1): asked before the microphone
  // opens, and again if start-upload answers 403 ai_disclosure_required.
  const { ensureAiDisclosure, aiDisclosureDialog } = useAiDisclosureGate({ user, setUser, refreshUser });
  const { usage } = useUsage(subsEnabled);
  // Ops kill switch for the in-recorder tools panel. Off, the entry points and
  // the panel disappear from this screen; /tools is unaffected either way.
  const toolsAvailable = recordingToolsEnabled(user);
  const openTools = useCallback((tab) => {
    setToolsTab(tab);
    setToolsOpen(true);
  }, []);

  const MAX_RECORDING_SECONDS = 120 * 60; // 120 minutes
  const MIN_RECORDING_SECONDS = 10; // mirrors the server's start-upload minimum
  // Speech doesn't need the MediaRecorder default (~128 kbps): 48 kbps opus
  // keeps transcription quality while shrinking uploads and the saved queue.
  const AUDIO_BITS_PER_SECOND = 48000;
  // How often the in-progress capture is persisted for crash recovery.
  const RECOVERY_FLUSH_SECONDS = 30;
  // Uploaded-file size ceiling. Compressed formats stay far under this even
  // at the 120-minute cap; it exists to stop an hour-long WAV from burning
  // upload time only to fail.
  const MAX_UPLOAD_BYTES = 500 * 1024 * 1024;

  const elapsedSeconds = useCallback(() => {
    const active = segmentStartRef.current ? Date.now() - segmentStartRef.current : 0;
    return Math.floor((accumulatedMsRef.current + active) / 1000);
  }, []);

  const startTicker = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      const seconds = elapsedSeconds();
      setDuration(seconds);
      if (seconds - lastFlushSecondsRef.current >= RECOVERY_FLUSH_SECONDS) {
        flushRecoveryRef.current?.();
      }
    }, 1000);
  }, [elapsedSeconds]);

  /**
   * Fold the active segment into the accumulated total (pause/stop).
   * `atMs` caps the segment at an earlier instant — used when the capture
   * turns out to have died or gone silent while the tab was hidden, so the
   * dead stretch isn't counted as recorded lesson time.
   */
  const closeActiveSegment = useCallback((atMs) => {
    if (segmentStartRef.current) {
      accumulatedMsRef.current += Math.max(0, (atMs ?? Date.now()) - segmentStartRef.current);
      segmentStartRef.current = null;
    }
  }, []);

  const fetchStudents = useCallback(async (signal) => {
    try {
      const response = await api.get("/students", signal ? { signal } : {});
      setStudents(response.data);
      // Keep the selection pointing at the fresh record so the card's state
      // line and the gate reflect the latest server state.
      setSelectedStudent((prev) =>
        prev ? response.data.find((s) => s.student_id === prev.student_id) || null : prev
      );
    } catch (_error) {
      if (!signal?.aborted) {
        toast.error("Failed to load students");
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchStudents(controller.signal);
    return () => controller.abort();
  }, [fetchStudents]);

  // Today's sessions for the picker (skipped in class mode — the class target
  // is already fixed by the route).
  useEffect(() => {
    if (!SCHEDULING_ENABLED || isClassMode) return undefined;
    const controller = new AbortController();
    teachingToday({ signal: controller.signal })
      .then((data) => setScheduledToday(data?.occurrences || []))
      .catch(() => {});
    return () => controller.abort();
  }, [isClassMode]);

  useEffect(() => {
    if (preSelectedStudentId && students.length > 0) {
      const student = students.find(s => s.student_id === preSelectedStudentId);
      if (student) setSelectedStudent(student);
    }
  }, [preSelectedStudentId, students]);

  // Auto-prelink: a manually picked student with exactly ONE unrecorded
  // occurrence today gets linked to it automatically (the card then says
  // "Today 4:00 PM", so the link is visible), keeping program attribution from
  // depending entirely on the quick-pick habit. An effect rather than the
  // picker's handler so the /hans/:studentId deep-link path — where the student
  // lands before /schedule/today resolves — is covered too. Ambiguous (2+) or
  // already-recorded occurrences never auto-link, and an existing stamp is
  // never overridden (the picker's mismatch-clear runs first).
  useEffect(() => {
    if (!SCHEDULING_ENABLED || isClassMode || !selectedStudent || scheduledOccurrence) return;
    const candidates = scheduledToday.filter(
      (o) => o.student_id === selectedStudent.student_id && !o.lesson_id && !o.teaching_confirmed && !o.class_id && o.recordable !== false
    );
    if (candidates.length === 1) {
      setScheduledOccurrence(selectionFromOccurrence(candidates[0]));
    }
  }, [selectedStudent, scheduledToday, scheduledOccurrence, isClassMode]);

  // In class mode, fetch the class so we can show its name and gate recording
  // on every member having consent (mirrors the server start-upload check).
  useEffect(() => {
    if (!isClassMode) return undefined;
    const controller = new AbortController();
    api.get(`/classes/${classId}`, { signal: controller.signal })
      .then((res) => setCls(res.data))
      .catch((_err) => {
        if (!controller.signal.aborted) toast.error("Failed to load class");
      });
    return () => controller.abort();
  }, [isClassMode, classId]);

  useEffect(() => {
    if (!cls || students.length === 0) return;
    const ids = scheduledOccurrence?.appointmentId && scheduledOccurrence.studentIds ? scheduledOccurrence.studentIds : cls.member_ids || [];
    setClassMembers(ids.map((id) => students.find((s) => s.student_id === id)).filter(Boolean));
  }, [cls, students, scheduledOccurrence]);

  useEffect(() => {
    if (!scheduledOccurrence?.appointmentId && !scheduledOccurrence?.connectionId && scheduledOccurrence?.revision === undefined) {
      setSelectionError(null); setSelectionChecking(false); return undefined;
    }
    if (stage !== "select" || (!classId && !selectedStudent?.student_id)) return undefined;
    const controller = new AbortController();
    setSelectionChecking(true); setSelectionError(null);
    verifySelection(scheduledOccurrence, { classId, studentId: selectedStudent?.student_id }, { signal: controller.signal })
      .then((fresh) => { if (!controller.signal.aborted) setScheduledOccurrence(fresh); })
      .catch((error) => { if (!controller.signal.aborted) setSelectionError(error.message); })
      .finally(() => { if (!controller.signal.aborted) setSelectionChecking(false); });
    return () => controller.abort();
    // Metadata updates retain the same selected identity and must not refetch forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduledOccurrence?.connectionId, scheduledOccurrence?.appointmentId, scheduledOccurrence?.revision, classId, selectedStudent?.student_id, stage]);

  const pendingConsent = useMemo(
    () => classMembers.filter((s) => s.consent_status !== "granted"),
    [classMembers]
  );
  const expectedClassMembers = scheduledOccurrence?.appointmentId && scheduledOccurrence.studentIds
    ? scheduledOccurrence.studentIds.length : (cls?.member_ids || []).length;
  const classConsentOk = isClassMode && classMembers.length > 0 && classMembers.length === expectedClassMembers && pendingConsent.length === 0;

  // Spec 10: "Write notes instead" opens the typed (no-audio) lesson compose
  // page, carrying the current target and any linked occurrence so the typed
  // lesson lands stamped exactly like a recording would.
  const openManualCompose = useCallback(() => {
    const params = new URLSearchParams(
      isClassMode ? { class: classId } : { student: selectedStudent?.student_id || "" }
    );
    for (const [key, value] of selectionParams(scheduledOccurrence, "slot")) params.set(key, value);
    navigate(`/lesson/new?${params.toString()}`);
  }, [isClassMode, classId, selectedStudent, scheduledOccurrence, navigate]);

  /**
   * Send (or re-send) the recording consent request for a student, from the
   * saved stage, so a blocked take can be unblocked without leaving here.
   */
  const resendConsent = useCallback(
    async (student) => {
      if (!student?.student_id || consentSending) return;
      setConsentSending(true);
      try {
        const res = await api.post(`/students/${student.student_id}/consent/resend`);
        toast.success(`Consent request sent to ${res.data?.sent_to || "the recipient"}.`);
      } catch (error) {
        toast.error(getErrorMessage(error, "Couldn't send the consent request."));
      } finally {
        setConsentSending(false);
        fetchStudents();
      }
    },
    [fetchStudents, consentSending]
  );

  const markExplainerSeen = useCallback(() => {
    if (explainerSeen) return;
    setExplainerSeen(true);
    try {
      localStorage.setItem(EXPLAINER_SEEN_KEY, "1");
    } catch {
      // Private mode or a full store: the line shows again next time, no harm.
    }
  }, [explainerSeen]);

  /**
   * Request microphone access and start recording audio.
   * Picks the best supported audio format (WebM or MP4 for Safari).
   * Starts a 1-second interval timer and transitions to the "recording" stage.
   */
  const startRecording = async () => {
    markExplainerSeen();
    if (selectionChecking || selectionError) { toast.error(selectionError || "Checking the selected appointment…"); return; }
    // Nothing left to record with, anywhere: say so before the microphone,
    // and point at the page that can change it. The server's gate backstops.
    if (recordingUsageLine(usage)?.exhausted) {
      setMinutesDialog({ orgPool: null, saved: false });
      return;
    }
    try {
      const fresh = await verifySelection(scheduledOccurrence, { classId, studentId: selectedStudent?.student_id });
      if (fresh?.consentReady === false) { toast.error("Recording consent is still needed for this booking."); return; }
      if (fresh?.appointmentId) setScheduledOccurrence(fresh);
    } catch (error) { setSelectionError(error.message); toast.error(error.message); return; }
    if (isClassMode) {
      // Mirror the server start-upload gate: every member needs consent.
      if (classMembers.length === 0) {
        toast.error("Add at least one student to record for this class.");
        return;
      }
      if (pendingConsent.length > 0) {
        const names = pendingConsent.map(studentName).join(", ");
        toast.error(`Recording is locked until every member has accepted consent. Awaiting: ${names}`);
        return;
      }
    } else if (!selectedStudent || selectedStudent.consent_status !== "granted") {
      // Mirror of the disabled-button gate (and the server's start-upload
      // check) so no other entry point can start a blocked recording.
      toast.error("Recording is locked until a consent form is accepted for this student.");
      return;
    }
    // Lesson audio goes to third-party AI; the instructor agrees to that
    // before the microphone opens. "Not now" leaves nothing started.
    if (!(await ensureAiDisclosure())) return;
    try {
      // Discard any capture left over from a previous attempt (e.g. an
      // upload the consent gate rejected) — stale state here would hide
      // the stop controls and upload the old audio under this student.
      setAudioBlob(null);
      setDuration(0);
      setUploadProgress(0);
      setPickedUpload(null);
      setTakeKept(false);
      setKeptReason(null);
      audioChunksRef.current = [];
      accumulatedMsRef.current = 0;
      segmentStartRef.current = null;
      hiddenAtRef.current = null;
      recoveryIdRef.current = null;
      lastFlushSecondsRef.current = 0;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setCaptureStream(stream);

      // Pick a supported mime type (Safari records MP4/AAC, not WebM)
      const mimeType = pickAudioMimeType();
      mimeTypeRef.current = mimeType || "audio/webm";

      const mediaRecorder = new MediaRecorder(
        stream,
        mimeType
          ? { mimeType, audioBitsPerSecond: AUDIO_BITS_PER_SECOND }
          : { audioBitsPerSecond: AUDIO_BITS_PER_SECOND }
      );
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      // The stopped take goes to review: listen, name it, save it.
      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeTypeRef.current });
        setAudioBlob(blob);
        setStage("review");
      };
      mediaRecorder.onerror = () => handleCaptureFailureRef.current?.();

      // iOS ends the mic track when the system takes the microphone away (a
      // phone call, Siri, another app) or keeps the page suspended too long.
      // Without this listener the recorder dies silently while the UI keeps
      // showing a live recording. track.stop() — our own cleanup — never
      // fires 'ended', so this only catches genuine external interruptions.
      const track = stream.getAudioTracks?.()[0] || stream.getTracks?.()[0] || null;
      captureTrackRef.current = track;
      track?.addEventListener?.("ended", () => handleCaptureFailureRef.current?.());

      recordingActiveRef.current = true;
      mediaRecorder.start(1000);
      segmentStartRef.current = Date.now();
      setIsRecording(true);
      setStage("recording");
      startTicker();
    } catch (_error) {
      toast.error("Could not access microphone");
    }
  };

  /**
   * Pause the active recording and stop the duration timer.
   */
  const pauseRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.pause();
      clearInterval(timerRef.current);
      closeActiveSegment();
      setDuration(elapsedSeconds());
      setIsPaused(true);
    }
  };

  /**
   * Resume a paused recording and restart the duration timer.
   */
  const resumeRecording = () => {
    if (mediaRecorderRef.current?.state === "paused") {
      mediaRecorderRef.current.resume();
      segmentStartRef.current = Date.now();
      startTicker();
      setIsPaused(false);
    }
  };

  /**
   * Stop recording, release the microphone, and prepare the audio blob for review.
   */
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current) {
      recordingActiveRef.current = false;
      // The recorder stops itself when its track dies (screen lock, phone
      // call); calling stop() again would throw InvalidStateError.
      if (mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      clearInterval(timerRef.current);
      closeActiveSegment();
      setDuration(elapsedSeconds());
      streamRef.current?.getTracks().forEach(track => track.stop());
      // Drop the tuner's borrowed reference in the same breath as the tracks,
      // so it stops rather than reading a dead stream.
      setCaptureStream(null);
      setIsRecording(false);
      setIsPaused(false);
      // Final recovery snapshot: the complete capture, so a tab closed
      // before the instructor presses Save can still be recovered.
      flushRecoveryRef.current?.();
    }
  }, [closeActiveSegment, elapsedSeconds]);
  stopRecordingRef.current = stopRecording;

  /**
   * The capture died out from under us — iOS took the microphone away
   * (screen lock, app switch, phone call, Siri) or the recorder errored.
   * Without this, the UI keeps showing a live recording (pulsing dot,
   * counting timer) while nothing is being captured, and the instructor
   * only finds out after the lesson. Stop honestly, keep the partial take,
   * and say what happened.
   */
  const handleCaptureFailureRef = useRef(null);
  handleCaptureFailureRef.current = () => {
    if (!recordingActiveRef.current) return; // our own stop, or already handled
    // iOS delivers the event only once the page resumes — don't count the
    // locked/backgrounded stretch as recorded audio.
    closeActiveSegment(hiddenAtRef.current ?? undefined);
    hiddenAtRef.current = null;
    stopRecording(); // also flushes the final recovery snapshot
    toast.error(
      "Recording stopped — your device paused the microphone (screen lock, phone call, or app switch). The audio captured so far is preserved."
    );
  };

  // Auto-stop recording at 120-minute cap
  useEffect(() => {
    if (isRecording && !isPaused && duration >= MAX_RECORDING_SECONDS) {
      stopRecordingRef.current?.();
      toast.info("Recording stopped — 120-minute limit reached.");
    }
  }, [duration, isRecording, isPaused, MAX_RECORDING_SECONDS]);

  // A browser can be shared, so a saved recording belongs to the signed-in
  // instructor who recorded it, and only its owner may send it (as on iOS);
  // signing out leaves every recording saved for its owner.
  const accountId = user?.user_id || null;
  const ownsUpload = useCallback((entry) => (entry?.accountId || null) === accountId, [accountId]);

  // The review stage's player: an object URL for the blob, revoked with it.
  const [previewUrl, setPreviewUrl] = useState(null);
  useEffect(() => {
    if (!audioBlob || typeof URL.createObjectURL !== "function") {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(audioBlob);
    setPreviewUrl(url);
    return () => {
      URL.revokeObjectURL?.(url);
    };
  }, [audioBlob]);

  /**
   * Snapshot the current capture (blob + target metadata) into a queue entry,
   * so the same shape drives both the live upload and a later retry.
   */
  const buildUploadEntry = useCallback((blob) => {
    const contentType = mimeTypeRef.current;
    const stamp = scheduledOccurrence
      ? { scheduledSelection: scheduledOccurrence, scheduledSlotId: scheduledOccurrence.slotId, scheduledDate: scheduledOccurrence.date }
      : {};
    if (isClassMode) {
      // Class target is fixed by the route, so a re-record here always stays on
      // the same class lesson.
      return {
        kind: "class",
        accountId,
        classId,
        title: lessonTitle || `${cls?.name || "Class"} lesson`,
        instrument: selectedInstrument,
        duration,
        contentType,
        label: cls?.name ? `${cls.name} (class)` : "Class lesson",
        blob,
        ...(rerecordLessonId ? { rerecordLessonId } : {}),
        ...stamp,
      };
    }
    const name = studentName(selectedStudent);
    // Re-record only rides along while the selection still matches the lesson's
    // student (the route param it was opened with) — switching students in the
    // picker turns it back into a fresh recording of the new student.
    const rerecord =
      rerecordLessonId && selectedStudent.student_id === preSelectedStudentId ? { rerecordLessonId } : {};
    return {
      kind: "student",
      accountId,
      studentId: selectedStudent.student_id,
      title: lessonTitle || `Lesson with ${name}`,
      instrument: selectedInstrument,
      duration,
      contentType,
      label: name || "Student lesson",
      blob,
      ...rerecord,
      ...stamp,
    };
  }, [accountId, isClassMode, classId, cls, lessonTitle, selectedInstrument, duration, selectedStudent, scheduledOccurrence, rerecordLessonId, preSelectedStudentId]);
  buildUploadEntryRef.current = buildUploadEntry;

  /**
   * Persist the capture so far to the upload queue (crash recovery). An
   * in-progress recording otherwise lives only in memory: iOS Safari
   * jettisons suspended tabs freely, fires no beforeunload, and a reload
   * mid-lesson used to cost the entire recording. Chunks arrive every second
   * (the timeslice above) and concatenate to a decodable prefix in both WebM
   * and Safari's fragmented MP4, so a partial flush is transcribable. Runs
   * every RECOVERY_FLUSH_SECONDS from the ticker, when the tab hides (the
   * moment of greatest risk), and on capture death — upserting one entry per
   * capture, removed again when the live upload succeeds.
   */
  const flushRecovery = useCallback(() => {
    if (audioChunksRef.current.length === 0) return flushPromiseRef.current;
    const seconds = elapsedSeconds();
    // The server rejects captures under the minimum — don't save junk a
    // retry could never upload.
    if (seconds < MIN_RECORDING_SECONDS) return flushPromiseRef.current;
    lastFlushSecondsRef.current = seconds;
    const blob = new Blob(audioChunksRef.current, { type: mimeTypeRef.current });
    const entry = { ...buildUploadEntryRef.current(blob), duration: seconds };
    if (recoveryIdRef.current) entry.id = recoveryIdRef.current;
    flushPromiseRef.current = uploadQueue
      .enqueue(entry)
      .then((saved) => {
        recoveryIdRef.current = saved.id;
      })
      .catch(() => {}); // best-effort — never disturb the recording
    return flushPromiseRef.current;
  }, [elapsedSeconds, MIN_RECORDING_SECONDS]);
  flushRecoveryRef.current = flushRecovery;

  /**
   * Monotonic sink for upload progress. Byte events and the per-part floor
   * are two independent sources and can briefly disagree (a floor lands while
   * a late event from the same part is still queued), and a bar that slides
   * backwards reads as a failing upload.
   */
  const reportUploadProgress = useCallback((pct) => {
    setUploadProgress((prev) => (pct > prev ? pct : prev));
  }, []);

  const uploadingRef = useRef(false);

  const goToLesson = useCallback((entry, lessonId) => {
    toast.success("Processing started");
    setSavedTake(null);
    setStage("complete");
    uploadingRef.current = false;
    const dest = entry.kind === "class" ? `/lesson/${lessonId}?kind=class` : `/lesson/${lessonId}`;
    navigateTimeoutRef.current = setTimeout(() => navigate(dest), 2000);
  }, [navigate]);

  /**
   * The saved stage: the take is in this browser, for this one reason. The
   * reason is written on the queue entry too, so the Saved recordings page
   * says the same thing.
   */
  const showSaved = useCallback(async (entry, blocked) => {
    await uploadQueue.setBlocked(entry.id, blocked).catch(() => {});
    setSavedTake({ entry: { ...entry, blocked }, blocked });
    setUploadProgress(0);
    setStage("saved");
    uploadingRef.current = false;
  }, []);

  /**
   * Queue-persist an entry and run the upload pipeline, routing success and
   * failure UI. Shared by the live-capture path and the picked-file path so
   * their failure handling (a consent 403, a network drop → saved for retry)
   * can't drift apart. The capture is persisted BEFORE the upload starts
   * (and removed again on success), so a stale-consent 403, a network drop,
   * or a tab closed mid-upload never loses a full lesson.
   */
  const runEntryUpload = async (entry) => {
    uploadingRef.current = true;
    setStage("uploading");
    setUploadProgress(0);
    setKeptReason(null);
    const saved = await uploadQueue.enqueue(entry).catch(() => null);
    try {
      const lessonId = await performUpload(saved || entry, reportUploadProgress);
      if (saved) {
        await uploadQueue.remove(saved.id).catch(() => {});
      } else if (entry.id) {
        // The upsert write failed but earlier recovery flushes may have
        // landed — clear them so the uploaded lesson can't resurface as a
        // phantom "saved recording".
        await uploadQueue.remove(entry.id).catch(() => {});
      }
      setAudioBlob(null);
      audioChunksRef.current = [];
      setPickedUpload(null);
      setTakeKept(false);
      goToLesson(entry, lessonId);
    } catch (error) {
      const blocked = blockedReason(error);
      setUploadProgress(0);
      // Local consent data is stale (the gate here allowed recording) —
      // refetch so the card and the saved stage say what the server knows.
      if (blocked.code === "consent_required") fetchStudents();
      if (saved) {
        // Only a capture the queue holds may leave the screen.
        setAudioBlob(null);
        audioChunksRef.current = [];
        setPickedUpload(null);
        await showSaved(saved, blocked);
      } else {
        // The queue write failed: the take stays here (the reviewed take, or
        // the picked file on Select) with its reason, to be sent again
        // rather than lost.
        setKeptReason(blocked);
        setTakeKept(true);
        setStage(pickedUpload ? "select" : "review");
        uploadingRef.current = false;
      }
      if (blocked.code === "ai_disclosure_required") {
        // The server wants the AI disclosure accepted first; ask, and upload
        // the same capture again on agreement. A refusal leaves the take
        // where it is, saved or on review, with the notice as its reason.
        if (await ensureAiDisclosure({ force: true })) {
          if (saved) await retryUploadRef.current?.(saved);
          else await runEntryUpload(entry);
        }
      }
    }
  };

  const uploadRecording = async () => {
    if (!audioBlob || uploadingRef.current) return;
    if (!isClassMode && !selectedStudent) return;
    if (duration < MIN_RECORDING_SECONDS) {
      // The server rejects short recordings with a 400 — don't queue a
      // capture whose retry can never succeed.
      toast.error(`Recordings must be at least ${MIN_RECORDING_SECONDS} seconds to process.`);
      return;
    }
    uploadingRef.current = true;
    // Wait out any in-flight recovery flush so its write can't land after
    // (and resurrect) the entry this upload removes on success.
    if (flushPromiseRef.current) await flushPromiseRef.current.catch(() => {});
    const entry = buildUploadEntry(audioBlob);
    // Reuse the crash-recovery entry's id so this enqueue replaces it
    // instead of saving the same capture twice.
    if (recoveryIdRef.current) entry.id = recoveryIdRef.current;
    recoveryIdRef.current = null; // from here the queue entry owns the capture
    await runEntryUpload(entry);
  };

  /**
   * Validate a picked audio file (type, size, measurable duration, length
   * caps) and stage it for the Save confirmation. Uploaded audio covers
   * what live capture can't: Voice Memos made with the phone locked (the
   * iOS answer), Zoom exports, dedicated recorders. The server re-measures
   * duration from the transcription, so the metadata read here only needs
   * to be honest enough for the caps.
   */
  const onUploadFilePicked = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-picking the same file
    if (!file) return;
    const contentType = resolveAudioUploadType(file);
    if (!contentType) {
      toast.error("That file doesn't look like audio. Supported: M4A, MP3, WAV, AAC, OGG, FLAC, WebM.");
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error("That file is too large (over 500 MB). Export it as M4A or MP3 and try again.");
      return;
    }
    let seconds;
    try {
      seconds = Math.round(await readAudioDuration(file));
    } catch (_error) {
      toast.error("Couldn't read that audio file. Convert it to M4A or MP3 and try again.");
      return;
    }
    if (seconds < MIN_RECORDING_SECONDS) {
      toast.error(`Recordings must be at least ${MIN_RECORDING_SECONDS} seconds to process.`);
      return;
    }
    if (seconds > MAX_RECORDING_SECONDS) {
      toast.error("Recordings are capped at 120 minutes. Trim the file and try again.");
      return;
    }
    setKeptReason(null);
    setPickedUpload({ file, duration: seconds, contentType, name: file.name });
  };

  /** Upload the staged file through the same pipeline as a live capture. */
  const processPickedUpload = async () => {
    if (!pickedUpload || uploadingRef.current) return;
    if (selectionChecking || selectionError) { toast.error(selectionError || "Checking the selected appointment…"); return; }
    let fresh;
    try {
      fresh = await verifySelection(scheduledOccurrence, { classId, studentId: selectedStudent?.student_id });
      if (fresh?.consentReady === false) { toast.error("Recording consent is still needed for this booking."); return; }
    } catch (error) { toast.error(getErrorMessage(error, "Refresh the selected appointment.")); return; }
    // Same consent gate as Record — an uploaded lesson is still a recording
    // of the student (the server's start-upload 403 backstops).
    if (isClassMode ? !classConsentOk : !selectedStudent || selectedStudent.consent_status !== "granted") {
      toast.error("Uploading is locked until a consent form is accepted.");
      return;
    }
    if (!(await ensureAiDisclosure())) return;
    const entry = {
      ...buildUploadEntry(pickedUpload.file),
      scheduledSelection: fresh,
      duration: pickedUpload.duration,
      contentType: pickedUpload.contentType,
    };
    await runEntryUpload(entry);
  };

  /**
   * Format seconds into MM:SS display string.
   * @param {number} seconds - Duration in seconds.
   * @returns {string} Formatted time string (e.g. "02:35").
   */
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  /**
   * Send a saved recording again from the persistent queue. A failure lands
   * on the saved stage with the new reason; a success opens the lesson.
   */
  const retryUpload = useCallback(async (entry) => {
    // Another instructor's take is never sent under this session.
    if (!ownsUpload(entry)) return;
    if (uploadingRef.current) return;
    // A saved recording still goes to the AI processors, so the same
    // agreement applies. "Not now" keeps it saved.
    if (!(await ensureAiDisclosure())) return;
    if (uploadingRef.current) return;
    uploadingRef.current = true;
    setStage("uploading");
    setUploadProgress(0);
    let blocked = null;
    try {
      const lessonId = await performUpload(entry, reportUploadProgress);
      await uploadQueue.remove(entry.id);
      goToLesson(entry, lessonId);
    } catch (error) {
      blocked = blockedReason(error);
      if (blocked.code === "consent_required") fetchStudents();
      await showSaved(entry, blocked);
    } finally {
      uploadingRef.current = false;
    }
    // Asked once the upload lock is released, so agreeing can retry at once.
    if (blocked?.code === "ai_disclosure_required") {
      if (await ensureAiDisclosure({ force: true })) await retryUploadRef.current?.(entry);
    }
  }, [ownsUpload, goToLesson, fetchStudents, reportUploadProgress, ensureAiDisclosure, showSaved]);
  const retryUploadRef = useRef(null);
  retryUploadRef.current = retryUpload;

  // A take on the saved stage belongs to whoever recorded it; when another
  // instructor's session takes over the tab, it leaves the screen unsent.
  useEffect(() => {
    if (stage === "saved" && savedTake && !ownsUpload(savedTake.entry)) {
      setSavedTake(null);
      setStage("select");
    }
  }, [stage, savedTake, ownsUpload]);

  /** Leave the saved stage; the take stays in Saved recordings. */
  const finishSaved = () => {
    setSavedTake(null);
    setStage("select");
  };

  /** Drop the current capture and start over. */
  const discardCapture = useCallback(() => {
    if (recoveryIdRef.current) {
      uploadQueue.remove(recoveryIdRef.current).catch(() => {});
      recoveryIdRef.current = null;
    }
    setAudioBlob(null);
    setDuration(0);
    audioChunksRef.current = [];
    setTakeKept(false);
    setKeptReason(null);
    setStage("select");
  }, []);

  /** Confirm before discarding — the recording can't be recovered once deleted. */
  const discardReview = () => {
    if (!window.confirm("Discard this recording? It is deleted from this browser and cannot be recovered.")) return;
    discardCapture();
  };

  // Warn before the tab closes while audio would be lost: an in-progress
  // recording lives only in memory, and an in-flight upload's queue entry
  // is still being written.
  useEffect(() => {
    if (!isRecording && stage !== "uploading") return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [isRecording, stage]);

  // iOS Safari suspends the page — and pauses the microphone — when the
  // screen locks or the instructor switches apps; wall-clock accrual would
  // silently count that dead air as recorded lesson time (duration_seconds
  // drives the usage quota). So: stamp the instant the tab hides, and if the
  // capture turns out to have been muted or killed while hidden, fold the
  // active segment at that stamp instead of at resume. Desktop browsers keep
  // capturing in hidden tabs (the track stays live and unmuted) and are
  // untouched. Hiding is also the moment of greatest risk of losing the tab
  // outright, so flush the crash-recovery snapshot on the way out —
  // beforeunload never fires on iOS, making pagehide/hidden the only signal.
  useEffect(() => {
    if (!isRecording) return undefined;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAtRef.current = Date.now();
        flushRecoveryRef.current?.();
        return;
      }
      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      const track = captureTrackRef.current;
      if (!hiddenAt || !segmentStartRef.current || !track) return;
      if (track.readyState === "ended" || track.muted) {
        accumulatedMsRef.current += Math.max(0, hiddenAt - segmentStartRef.current);
        // Capture usually resumes on return; restart the segment now unless
        // the track is dead (the 'ended' handler then finishes the stop).
        segmentStartRef.current = track.readyState === "ended" ? null : Date.now();
        setDuration(elapsedSeconds());
      }
    };
    const onPageHide = () => flushRecoveryRef.current?.();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      hiddenAtRef.current = null;
    };
  }, [isRecording, elapsedSeconds]);

  // Keep the screen awake while recording so a locked screen doesn't suspend
  // the tab and drop the lesson. The Screen Wake Lock sentinel is released
  // automatically when the tab is hidden, so we re-acquire on visibilitychange
  // once the tab is visible again. Silently no-ops where unsupported (iOS < 16.4).
  useEffect(() => {
    if (!isRecording) return undefined;
    if (!("wakeLock" in navigator)) return undefined;

    const acquire = async () => {
      try {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      } catch {
        // Denied (e.g. low battery) or unsupported — recording continues,
        // the screen just isn't kept on. Not worth surfacing to the user.
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") acquire();
    };

    acquire();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
    };
  }, [isRecording]);

  // Clean up MediaRecorder and microphone on unmount
  useEffect(() => {
    return () => {
      recordingActiveRef.current = false; // silence any late 'ended' event
      clearInterval(timerRef.current);
      clearTimeout(navigateTimeoutRef.current);
      if (mediaRecorderRef.current?.state !== "inactive") {
        mediaRecorderRef.current?.stop();
      }
      streamRef.current?.getTracks().forEach(track => track.stop());
    };
  }, []);

  /**
   * Leave the recording screen. Confirm before abandoning a live recording;
   * on leave, snapshot the capture so far into the upload queue so the audio
   * survives as a saved recording (an in-flight upload is already persisted
   * to the queue, so it needs no guard).
   *
   * Go back to wherever the instructor came from (student profile, schedule,
   * the sidebar CTA, …). When there's no in-app history — a deep link, a
   * refresh, or a fresh tab, where React Router leaves `location.key` at its
   * initial "default" — `navigate(-1)` would leave the app, so fall back to
   * the dashboard.
   */
  const handleBack = useCallback(() => {
    if (isRecording) {
      const willSave = elapsedSeconds() >= MIN_RECORDING_SECONDS;
      const ok = window.confirm(
        willSave
          ? "Leave without finishing? Recording will stop — the audio captured so far is kept under Saved recordings."
          : "Leave without finishing? Your in-progress recording will be lost."
      );
      if (!ok) return;
      if (willSave) flushRecoveryRef.current?.();
    }
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate("/dashboard");
    }
  }, [isRecording, navigate, location.key, elapsedSeconds, MIN_RECORDING_SECONDS]);

  // ─── Picking who ───────────────────────────────────────────

  /**
   * A student from the picker. In class mode the class is fixed by the
   * route, so a student pick leaves the class flow for that student's own
   * recorder.
   */
  const pickStudent = (student) => {
    setPickerOpen(false);
    if (isClassMode) {
      setScheduledOccurrence(null);
      navigate(`/hans/${student.student_id}`);
      return;
    }
    setSelectedStudent(student);
    // Default to the remembered instrument or auto-detect
    setSelectedInstrument(student?.default_instrument || "auto");
    // A manual pick breaks the link to a scheduled occurrence unless it
    // re-selects the same student.
    setScheduledOccurrence((prev) => {
      if (!prev) return prev;
      const occ = scheduledToday.find((o) => o.slot_id === prev.slotId && o.date === prev.date);
      return occ && occ.student_id === student.student_id ? prev : null;
    });
  };

  /** One of today's sessions from the picker (Spec 04). */
  const pickOccurrence = (occ) => {
    setPickerOpen(false);
    if (occ.class_id || occ.recordable === false) {
      navigate(recordHrefForOccurrence(occ));
      return;
    }
    const student = students.find((s) => s.student_id === occ.student_id);
    if (student) {
      setSelectedStudent(student);
      setSelectedInstrument(student.default_instrument || "auto");
    }
    setLessonTitle(`${weekdayName(occ.date)} lesson`);
    setScheduledOccurrence(selectionFromOccurrence(occ));
  };

  const onAttendanceChange = (fresh) => {
    setScheduledOccurrence(fresh);
    const params = new URLSearchParams(searchParams);
    for (const [key, value] of selectionParams(fresh)) params.set(key, value);
    setSearchParams(params, { replace: true });
  };

  // ─── What the select stage says ────────────────────────────

  const targetName = isClassMode ? (cls?.name || "Class") : studentName(selectedStudent);
  const session = isClassMode && scheduledOccurrence?.classSession ? scheduledOccurrence : null;
  const roll = session ? attendanceSummary(session, students) : null;
  const pickedToday = scheduledOccurrence ? scheduledToday.find((occ) => sameSelection(scheduledOccurrence, occ)) : null;
  const pickedTime = pickedToday ? formatTime12h(pickedToday.time_local) : "";
  const studentFirst = (selectedStudent?.first_name || "").trim() || studentName(selectedStudent).split(/\s+/)[0] || "";

  const recordHeld = selectionChecking || !!selectionError || (isClassMode
    ? !classConsentOk
    : (!selectedStudent || selectedStudent.consent_status !== "granted"));
  let recordHint = "Tap to record";
  if (selectionChecking) recordHint = "Checking the appointment…";
  else if (isClassMode && recordHeld) recordHint = classMembers.length === 0 ? "Add students to this class to record." : "Record opens once everyone here has consent.";
  else if (!isClassMode && !selectedStudent) recordHint = students.length === 0 ? "Add a student to record." : "Choose a student to record.";
  else if (!isClassMode && recordHeld) recordHint = `Record opens once ${studentFirst || "their"}’s consent is on file.`;

  const instrumentLine = selectedStudent
    ? (selectedInstrument && selectedInstrument !== "auto"
      ? selectedInstrument
      : (selectedStudent.instruments?.length === 1 ? selectedStudent.instruments[0] : null))
    : null;
  const usageLine = recordingUsageLine(usage);
  const businessLine = SCHEDULER_VIEW_ENABLED
    ? (selectionChecking ? "Checking appointment…" : scheduledOccurrence?.contextLabel || (rerecordLessonId ? "Keeping this lesson’s business" : "Unassigned"))
    : null;
  const reviewInstruments = !isClassMode && selectedStudent?.instruments?.length > 1 ? selectedStudent.instruments : [];
  const defaultTitle = isClassMode ? `${cls?.name || "Class"} lesson` : `Lesson with ${targetName}`;
  const hasTarget = isClassMode || !!selectedStudent;

  // The saved stage reads its student from the fresh roster, so a consent
  // request sent from it changes what the stage says.
  const savedStudent = savedTake && savedTake.entry.kind !== "class"
    ? students.find((s) => s.student_id === savedTake.entry.studentId) || null
    : null;
  const savedFixNow = savedTake ? savedFix(savedTake.blocked, { student: savedStudent }) : null;
  const savedWho = savedTake
    ? (savedTake.entry.kind === "class"
      ? (cls?.name || (savedTake.entry.label || "Class").replace(/ \(class\)$/, ""))
      : (studentName(savedStudent) || savedTake.entry.label || "Student"))
    : "";

  const cardShell = "w-full rounded-2xl border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] text-left";
  const cardMain = "flex w-full items-center gap-3.5 px-4 py-3.5 transition-colors hover:bg-[var(--studio-interactive)] rounded-2xl";
  const avatarShell = "flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--studio-interactive)] text-base font-semibold text-[var(--accent-yellow-text)]";
  const secondaryButton = "w-full h-12 rounded-full touch-target bg-[var(--studio-raised)] text-[var(--text-primary)] border border-[var(--studio-divider-strong)] hover:bg-[var(--studio-interactive)] disabled:opacity-50";

  const renderWhoCard = () => {
    if (isClassMode) {
      const sub = [
        pickedTime ? `Today ${pickedTime}` : null,
        `${classMembers.length} ${classMembers.length === 1 ? "student" : "students"}`,
        !session && pendingConsent.length ? `${pendingConsent.length} need${pendingConsent.length === 1 ? "s" : ""} consent` : null,
      ].filter(Boolean).join(" · ");
      return (
        <div className={cardShell} data-testid="recording-target">
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className={cardMain}
            aria-label={`Teaching ${targetName}. Change who you’re teaching`}
            data-testid="select-student"
          >
            <span className={avatarShell}><Users className="h-5 w-5" aria-hidden="true" /></span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-base text-[var(--text-primary)]">{targetName}</span>
              <span className="block truncate text-[13px] text-[var(--text-tertiary)]" data-testid="recording-target-state">{sub}</span>
              {businessLine !== null && <span className="block text-[13px] text-[var(--text-tertiary)]" data-testid="recording-business-context">{businessLine}</span>}
              {rerecordLessonId && <span className="block text-[13px] text-[var(--accent-yellow-text)]" data-testid="rerecord-note">Recording again for this lesson</span>}
            </span>
            <ChevronRight className="h-5 w-5 shrink-0 text-[var(--text-tertiary)]" aria-hidden="true" />
          </button>
          {roll && (
            <button
              type="button"
              onClick={() => setAttendanceOpen(true)}
              className="flex w-full items-center gap-2 border-t border-[var(--studio-divider)] px-4 py-3 text-[13px] transition-colors hover:bg-[var(--studio-interactive)] rounded-b-2xl"
              aria-label={`Attendance: ${roll.line}`}
              data-testid="attendance-row"
            >
              <span className="text-[var(--text-secondary)]">Attendance</span>
              <span className="text-[var(--text-tertiary)]">·</span>
              <span className={`min-w-0 flex-1 truncate ${roll.waiting.length > 0 ? "text-[var(--state-warning)]" : "text-[var(--text-tertiary)]"}`}>{roll.line}</span>
              <ChevronRight className="h-4 w-4 shrink-0 text-[var(--text-tertiary)]" aria-hidden="true" />
            </button>
          )}
        </div>
      );
    }
    if (!selectedStudent) {
      const none = students.length === 0;
      return (
        <button
          type="button"
          onClick={none ? () => navigate("/students") : () => setPickerOpen(true)}
          className={`${cardShell} ${cardMain}`}
          data-testid="recording-target-empty"
        >
          <span className={`${avatarShell} text-[var(--text-tertiary)]`}>
            {none ? <UserPlus className="h-5 w-5" aria-hidden="true" /> : <User className="h-5 w-5" aria-hidden="true" />}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-base text-[var(--text-primary)]">{none ? "Add your first student" : "Choose who you’re teaching"}</span>
            <span className="block text-[13px] text-[var(--text-tertiary)]">{none ? "Recording starts with a student." : "Your students, and today’s lessons."}</span>
          </span>
          <ChevronRight className="h-5 w-5 shrink-0 text-[var(--text-tertiary)]" aria-hidden="true" />
        </button>
      );
    }
    const sub = [pickedTime ? `Today ${pickedTime}` : null, instrumentLine, consentStateLabel(selectedStudent)].filter(Boolean).join(" · ");
    return (
      <div className={cardShell} data-testid="recording-target">
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className={cardMain}
          aria-label={`Teaching ${targetName}. Change who you’re teaching`}
          data-testid="select-student"
        >
          <span className={avatarShell}>{studentInitial(selectedStudent)}</span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-base text-[var(--text-primary)]">{targetName}</span>
            <span className="block truncate text-[13px] text-[var(--text-tertiary)]" data-testid="recording-target-state">{sub}</span>
            {businessLine !== null && <span className="block text-[13px] text-[var(--text-tertiary)]" data-testid="recording-business-context">{businessLine}</span>}
            {rerecordLessonId && <span className="block text-[13px] text-[var(--accent-yellow-text)]" data-testid="rerecord-note">Recording again for this lesson</span>}
          </span>
          <ChevronRight className="h-5 w-5 shrink-0 text-[var(--text-tertiary)]" aria-hidden="true" />
        </button>
      </div>
    );
  };

  const renderSavedAction = () => {
    if (!savedFixNow?.action || !savedTake) return null;
    const primary = "w-full h-12 rounded-full bg-[var(--accent-yellow)] text-[var(--text-inverse)] font-medium active:scale-95 transition-transform touch-target haptic disabled:opacity-50";
    switch (savedFixNow.action) {
      case "retry":
        return <Button onClick={() => retryUpload(savedTake.entry)} className={primary} data-testid="saved-retry">Try again</Button>;
      case "consent":
        return (
          <Button onClick={() => resendConsent(savedStudent)} disabled={consentSending} className={primary} data-testid="saved-request-consent">
            {consentSending ? "Sending…" : savedFixNow.label}
          </Button>
        );
      case "plan":
        return <Button onClick={() => navigate("/settings")} className={primary} data-testid="saved-plan">Plan &amp; usage</Button>;
      case "notice":
        return (
          <Button
            onClick={async () => { if (await ensureAiDisclosure({ force: true })) await retryUpload(savedTake.entry); }}
            className={primary}
            data-testid="saved-review-notice"
          >
            Review notice
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[var(--studio-base)]" data-testid="hans-recording">
      <div className="flex flex-col min-h-screen no-overscroll">
      {/* Header (mobile) */}
      <header className="md:hidden flex items-center justify-between px-4 md:px-6 py-4 border-b border-[var(--studio-divider)] safe-area-padding">
        <Button
          variant="ghost"
          onClick={handleBack}
          className="text-[var(--text-tertiary)] active:text-[var(--text-primary)] p-2 touch-target"
          aria-label="Back to dashboard"
          data-testid="back-to-dashboard"
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-2">
          <img src="/logo192.png" alt="Hans" className="w-8 h-8 rounded-lg" />
        </div>
        <div className="w-10" />
      </header>

      {/* Back button (desktop) — the recording screen renders outside the app
          shell (no sidebar), so on md+ this is the only way off it. */}
      <button
        onClick={handleBack}
        className="hidden md:flex items-center gap-1.5 absolute top-6 left-6 z-10
                   h-10 pl-2 pr-4 rounded-full border border-[var(--studio-divider)]
                   bg-[var(--studio-raised)] text-[var(--text-secondary)]
                   hover:text-[var(--text-primary)] hover:bg-[var(--studio-interactive)]
                   transition-colors"
        aria-label="Back to dashboard"
        data-testid="back-to-dashboard-desktop"
      >
        <ChevronLeft className="w-5 h-5" />
        <span className="text-sm font-medium">Back</span>
      </button>

      {/* Practice tools (desktop) — mirrors the Back button opposite it. The
          mobile entry points are the footer buttons at the bottom of the page. */}
      {toolsAvailable && (
        <button
          onClick={() => openTools("metronome")}
          className="hidden md:flex items-center gap-1.5 absolute top-6 right-6 z-10
                     h-10 pl-4 pr-4 rounded-full border border-[var(--studio-divider)]
                     bg-[var(--studio-raised)] text-[var(--text-secondary)]
                     hover:text-[var(--text-primary)] hover:bg-[var(--studio-interactive)]
                     transition-colors"
          aria-label="Open practice tools"
          data-testid="open-recording-tools-desktop"
        >
          <Music className="w-4 h-4" />
          <span className="text-sm font-medium">Tools</span>
        </button>
      )}

      {/* Main */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 md:px-6 py-8 md:py-12 safe-area-padding">
        <AnimatePresence mode="wait">
          {/* Stage: select */}
          {stage === "select" && (
            <motion.div
              key="select"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full max-w-md"
            >
              <h1
                className="text-2xl md:text-3xl font-light text-[var(--text-primary)] mb-5"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Record
              </h1>
              <Kicker className="mb-2">You’re teaching</Kicker>
              {renderWhoCard()}
              {selectionError && <p role="alert" className="mt-3 text-sm text-[var(--state-warning)]">{selectionError}</p>}

              <div className="mt-8 flex flex-col items-center gap-4">
                <button
                  type="button"
                  onClick={startRecording}
                  disabled={recordHeld}
                  aria-label="Start recording"
                  className="flex h-24 w-24 items-center justify-center rounded-full bg-[var(--accent-yellow)] text-[var(--text-inverse)]
                             shadow-[0_0_40px_var(--accent-yellow-glow)] active:scale-95 transition-transform
                             disabled:opacity-40 disabled:shadow-none touch-target haptic"
                  data-testid="start-recording-button"
                >
                  <Mic className="h-10 w-10" aria-hidden="true" />
                </button>
                <p className="text-sm text-[var(--text-tertiary)]" data-testid="record-hint">{recordHint}</p>

                <div className="mt-2 flex w-full flex-col gap-3">
                  {/*
                   * Spec 10: the no-audio path is a permanent peer of Record, not a
                   * consent-lock escape hatch. It also covers the lesson you forgot
                   * to record, the device that died, and writing up a past lesson —
                   * so the copy stays cause-neutral.
                   */}
                  <Button
                    variant="ghost"
                    onClick={openManualCompose}
                    disabled={!hasTarget}
                    className={secondaryButton}
                    data-testid={isClassMode ? "add-class-lesson-notes-button" : "add-lesson-notes-button"}
                  >
                    <Pencil className="w-4 h-4 mr-2" /> Write notes instead
                  </Button>

                  {/*
                   * Lesson audio upload: same pipeline and consent gate as live
                   * recording, for audio captured elsewhere — Voice Memos made
                   * with the phone locked (the iOS answer), Zoom exports,
                   * dedicated recorders.
                   */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="audio/*,.m4a,.mp3,.wav,.webm,.ogg,.opus,.flac,.aac"
                    className="hidden"
                    onChange={onUploadFilePicked}
                    data-testid="upload-audio-input"
                  />
                  <Button
                    variant="ghost"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={recordHeld}
                    className={secondaryButton}
                    data-testid="upload-audio-button"
                  >
                    <Upload className="w-4 h-4 mr-2" /> Upload a recording
                  </Button>

                  {pickedUpload && (
                    <div
                      className="p-3 rounded-xl bg-[var(--studio-raised)] border border-[var(--studio-divider-strong)] text-left"
                      data-testid="picked-upload"
                    >
                      <p className="text-sm font-medium text-[var(--text-primary)] truncate">{pickedUpload.name}</p>
                      <p className="mt-1 font-mono text-xs text-[var(--text-tertiary)]">
                        {formatTime(pickedUpload.duration)}
                      </p>
                      <p className="mt-2 text-xs text-[var(--text-tertiary)]">
                        By saving this file you confirm it’s a lesson recorded with the consent on
                        file for {isClassMode ? "every class member" : "this student"}.
                      </p>
                      {keptReason && (
                        <p role="alert" className="mt-2 text-xs text-[var(--state-warning)]" data-testid="kept-reason">
                          {`${savedFix(keptReason, { student: isClassMode ? null : selectedStudent }).line}. ${STILL_HERE}`}
                        </p>
                      )}
                      <div className="mt-3 flex items-center gap-4">
                        <Button
                          onClick={processPickedUpload}
                          className="h-11 px-6 bg-[var(--accent-yellow)] text-[var(--text-inverse)] font-medium rounded-full
                                     active:scale-95 transition-transform touch-target haptic"
                          data-testid="process-upload-button"
                        >
                          <Save className="w-4 h-4 mr-2" /> Save lesson
                        </Button>
                        <button
                          onClick={() => { setPickedUpload(null); setKeptReason(null); }}
                          className="underline text-[var(--text-tertiary)]"
                          data-testid="remove-picked-upload"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {!explainerSeen && (
                  <p className="mt-1 text-center text-[13px] leading-5 text-[var(--text-tertiary)]" data-testid="first-visit-line">
                    {FIRST_VISIT_LINE}
                  </p>
                )}
              </div>

              {usageLine && (
                <div className="mt-10 flex flex-col items-center" data-testid="recording-usage">
                  {usageLine.fraction !== null && (
                    <div
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={Math.round(usageLine.fraction * 100)}
                      aria-label="Recording minutes left"
                      className="mb-2 h-[2px] w-40 overflow-hidden rounded-full bg-[var(--studio-divider-strong)]"
                    >
                      <div className="h-full bg-[var(--accent-yellow)]" style={{ width: `${Math.round(usageLine.fraction * 100)}%` }} />
                    </div>
                  )}
                  <p className="text-xs text-[var(--text-tertiary)]">
                    {usageLine.text}
                    {" · "}
                    <button
                      type="button"
                      onClick={() => navigate("/settings")}
                      className="underline text-[var(--accent-yellow-text)]"
                      data-testid="recording-usage-plan"
                    >
                      Plan &amp; usage
                    </button>
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {/* Stage: recording */}
          {stage === "recording" && (
            <motion.div
              key="recording"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-lg text-center"
            >
              <p className="text-[var(--text-quaternary)] text-sm mb-1">Recording with</p>
              <h2 className="text-lg md:text-xl font-medium text-[var(--text-primary)] mb-6 md:mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {targetName}
              </h2>

              <SoundWave active={isRecording && !isPaused} />

              {/* Timer */}
              <div className="mt-6 md:mt-8 mb-8 md:mb-12">
                <div className="flex items-center justify-center gap-3">
                  {isRecording && !isPaused && (
                    <span className="w-2 h-2 bg-[var(--state-danger)] rounded-full recording-pulse" />
                  )}
                  <span
                    className="text-5xl md:text-6xl font-mono font-light text-[var(--text-primary)] tracking-tight"
                    data-testid="recording-timer"
                  >
                    {formatTime(duration)}
                  </span>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center justify-center gap-4 md:gap-6">
                <Button
                  onClick={isPaused ? resumeRecording : pauseRecording}
                  className="w-14 h-14 md:w-14 md:h-14 rounded-full bg-[var(--studio-interactive)] active:bg-[var(--studio-overlay)] border-0 touch-target haptic"
                  aria-label={isPaused ? "Resume recording" : "Pause recording"}
                  data-testid={isPaused ? "resume-button" : "pause-button"}
                >
                  {isPaused ? (
                    <Play className="w-6 h-6 text-[var(--text-primary)]" />
                  ) : (
                    <Pause className="w-6 h-6 text-[var(--text-primary)]" />
                  )}
                </Button>

                <Button
                  onClick={stopRecording}
                  className="w-20 h-20 rounded-full bg-[var(--accent-yellow)] active:brightness-95
                             shadow-[0_0_40px_var(--accent-yellow-glow)] touch-target haptic"
                  aria-label="Stop recording"
                  data-testid="stop-recording-button"
                >
                  <Square className="w-8 h-8 text-[var(--text-inverse)] fill-[var(--text-inverse)]" />
                </Button>
              </div>

              {isPaused && <p className="text-[var(--text-quaternary)] mt-6 text-sm">Paused</p>}

              {/*
               * iOS pauses or kills the microphone the moment the screen
               * locks, the instructor switches apps, or another tab takes
               * over — a web page cannot record in the background there.
               * Say so while the phone is most likely about to be set down,
               * instead of letting a truncated lesson teach it.
               */}
              {iosBrowser && isRecording && (
                <p
                  className="mt-6 mx-auto max-w-sm text-sm text-[var(--text-tertiary)]"
                  data-testid="ios-recording-note"
                >
                  Keep this screen open with the screen on — iPhone and iPad browsers pause the
                  microphone if the screen locks or you switch apps. To record with the screen
                  locked, use the Hans iOS app.
                </p>
              )}
            </motion.div>
          )}

          {/* Stage: review */}
          {stage === "review" && (
            <motion.div
              key="review"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full max-w-md text-center"
              data-testid="review-stage"
            >
              <Kicker>Review</Kicker>
              <h2 className="mt-2 text-lg md:text-xl font-medium text-[var(--text-primary)]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {targetName}
              </h2>
              <p className="mt-1 font-mono text-3xl font-light tracking-tight text-[var(--text-primary)]" data-testid="review-duration">
                {formatTime(duration)}
              </p>

              {duration < MIN_RECORDING_SECONDS ? (
                <div className="mt-8 text-center" data-testid="recording-too-short">
                  <p className="text-[var(--text-tertiary)] text-sm mb-4">
                    Too short to process — lessons must be at least {MIN_RECORDING_SECONDS} seconds.
                  </p>
                  <Button
                    onClick={discardCapture}
                    className="h-12 px-8 bg-[var(--studio-interactive)] text-[var(--text-primary)] font-medium rounded-full
                               active:scale-95 transition-transform touch-target haptic"
                    data-testid="discard-capture-button"
                  >
                    Discard recording
                  </Button>
                </div>
              ) : (
                <>
                  <audio
                    controls
                    src={previewUrl || undefined}
                    className="mx-auto mt-6 w-full"
                    aria-label="Listen to the recording"
                    data-testid="review-player"
                  />

                  <div className="mt-6 text-left">
                    <Kicker className="mb-2">Lesson title</Kicker>
                    <Input
                      placeholder={defaultTitle}
                      value={lessonTitle}
                      onChange={(e) => setLessonTitle(e.target.value)}
                      aria-label="Lesson title"
                      className="bg-[var(--studio-raised)] border-[var(--studio-divider-strong)] text-[var(--text-primary)] h-12 text-base placeholder:text-[var(--text-quaternary)]"
                      data-testid="lesson-title-input"
                    />
                  </div>

                  {reviewInstruments.length > 0 && (
                    <div className="mt-5 text-left">
                      <Kicker className="mb-2">Instrument</Kicker>
                      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Instrument" data-testid="instrument-chips">
                        {[...reviewInstruments, "auto"].map((value) => {
                          const on = selectedInstrument === value;
                          return (
                            <button
                              key={value}
                              type="button"
                              role="radio"
                              aria-checked={on}
                              onClick={() => setSelectedInstrument(value)}
                              className={[
                                "rounded-full border px-3.5 py-1.5 text-sm transition-colors",
                                on
                                  ? "border-[var(--accent-yellow-border-strong)] bg-[var(--accent-yellow-faint)] text-[var(--accent-yellow-text)]"
                                  : "border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] text-[var(--text-secondary)] hover:bg-[var(--studio-interactive)]",
                              ].join(" ")}
                              data-testid={`instrument-${value}`}
                            >
                              {value === "auto" ? "Let Hans tell" : value}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {keptReason && (
                    <p role="alert" className="mt-5 text-sm text-[var(--state-warning)]" data-testid="kept-reason">
                      {`${savedFix(keptReason, { student: isClassMode ? null : selectedStudent }).line}. ${STILL_HERE}`}
                    </p>
                  )}

                  <Button
                    onClick={uploadRecording}
                    className="mt-6 w-full h-14 bg-[var(--accent-yellow)] text-[var(--text-inverse)] font-medium rounded-full
                               active:scale-95 transition-transform
                               shadow-[0_0_40px_var(--accent-yellow-glow)] touch-target haptic"
                    data-testid="upload-recording-button"
                  >
                    <Check className="w-5 h-5 mr-2" /> {takeKept ? "Try again" : "Save lesson"}
                  </Button>
                  <button
                    type="button"
                    onClick={discardReview}
                    className="mt-4 py-2 text-sm text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                    data-testid="discard-button"
                  >
                    Discard
                  </button>
                </>
              )}
            </motion.div>
          )}

          {/* Stage: uploading */}
          {stage === "uploading" && (
            <motion.div
              key="uploading"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <Loader2 className="w-10 h-10 text-[var(--accent-yellow-text)] animate-spin mx-auto mb-6" />
              <h2 className="text-xl font-light text-[var(--text-primary)]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {uploadProgress < 100 ? "Uploading..." : "Processing..."}
              </h2>
              {uploadProgress < 100 && (
                <div className="w-48 mx-auto mt-4" data-testid="upload-progress">
                  <div className="h-1 rounded-full overflow-hidden bg-[var(--studio-interactive)]">
                    {uploadProgress === 0 ? (
                      // Nothing is confirmed sent yet, so quoting "0%" claims a
                      // measurement we don't have and reads as a stalled
                      // upload. Pulse until the first real byte count lands.
                      <div className="h-full w-1/3 rounded-full bg-[var(--accent-yellow)] animate-pulse" />
                    ) : (
                      <motion.div
                        className="h-full rounded-full bg-[var(--accent-yellow)]"
                        initial={{ width: 0 }}
                        animate={{ width: `${uploadProgress}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    )}
                  </div>
                  <p className="mt-2 text-sm text-[var(--text-tertiary)]">
                    {uploadProgress === 0 ? "Preparing upload…" : `${uploadProgress}%`}
                  </p>
                </div>
              )}
              {uploadProgress >= 100 && (
                <p className="text-[var(--text-quaternary)] mt-2 text-sm">Hans is transcribing your lesson</p>
              )}
            </motion.div>
          )}

          {/* Stage: saved in this browser */}
          {stage === "saved" && savedTake && (
            <motion.div
              key="saved"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full max-w-md text-center"
              data-testid="saved-stage"
            >
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[var(--accent-yellow-faint)] text-[var(--accent-yellow-text)]">
                <Download className="h-8 w-8" aria-hidden="true" />
              </span>
              <h2 className="mt-5 text-xl font-light text-[var(--text-primary)]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Saved in this browser
              </h2>
              <p className="mt-2 text-sm leading-6 text-[var(--text-secondary)]" data-testid="saved-reason">
                {savedReasonLine(savedTake.blocked, {
                  first: savedTake.entry.kind === "class" ? null : (savedStudent?.first_name || null),
                  student: savedStudent,
                })}
              </p>
              <div className={`${cardShell} mt-6 flex items-center gap-3.5 px-4 py-3.5`} data-testid="saved-take">
                <span className={avatarShell}>
                  {savedTake.entry.kind === "class" ? <Users className="h-5 w-5" aria-hidden="true" /> : studentInitial(savedStudent) }
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-base text-[var(--text-primary)]">{savedTake.entry.title}</span>
                  <span className="block truncate text-[13px] text-[var(--text-tertiary)]">{savedWho} · {clipLength(savedTake.entry.duration)}</span>
                </span>
              </div>
              <div className="mt-6 flex flex-col gap-3">
                {renderSavedAction()}
                <Button
                  variant="ghost"
                  onClick={() => navigate("/recordings/saved")}
                  className={secondaryButton}
                  data-testid="saved-open-list"
                >
                  Saved recordings
                </Button>
                <button
                  type="button"
                  onClick={finishSaved}
                  className="py-2 text-sm text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                  data-testid="saved-done"
                >
                  Done
                </button>
              </div>
            </motion.div>
          )}

          {/* Complete */}
          {stage === "complete" && (
            <motion.div
              key="complete"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[var(--accent-yellow)] rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-[var(--text-inverse)]" />
              </div>
              <h2 className="text-xl font-light text-[var(--text-primary)]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Done
              </h2>
              <p className="text-[var(--text-quaternary)] mt-2 text-sm">Redirecting...</p>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer (mobile only). These open the in-page panel; they used to
          navigate() to /metronome and /tuner, which unmounts this screen and
          stops the recording without warning. */}
      {toolsAvailable && (
        <footer className="md:hidden flex items-center justify-center gap-8 px-4 py-4 border-t border-[var(--studio-divider)] safe-area-padding">
          <button
            onClick={() => openTools("metronome")}
            data-testid="open-metronome"
            className="text-[var(--text-tertiary)] active:text-[var(--text-secondary)] text-sm transition-colors touch-target py-2"
          >
            Metronome
          </button>
          <button
            onClick={() => openTools("tuner")}
            data-testid="open-tuner"
            className="text-[var(--text-tertiary)] active:text-[var(--text-secondary)] text-sm transition-colors touch-target py-2"
          >
            Tuner
          </button>
        </footer>
      )}
      </div>

      {/* Mounted for the whole session so the metronome keeps its tempo (and
          keeps clicking) while the panel is closed — see RecordingTools. */}
      {toolsAvailable && (
        <RecordingTools
          open={toolsOpen}
          tab={toolsTab}
          onTabChange={setToolsTab}
          onClose={() => setToolsOpen(false)}
          stream={captureStream}
        />
      )}

      <RecordingPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        students={students}
        selectedStudentId={isClassMode ? null : selectedStudent?.student_id || null}
        onPickStudent={pickStudent}
        today={SCHEDULING_ENABLED && !isClassMode ? scheduledToday : []}
        selection={scheduledOccurrence}
        onPickOccurrence={pickOccurrence}
        showContext={SCHEDULER_VIEW_ENABLED}
      />

      {isClassMode && (
        <RecordingAttendanceDialog
          open={attendanceOpen}
          onOpenChange={setAttendanceOpen}
          selection={scheduledOccurrence}
          onChange={onAttendanceChange}
          students={students}
          disabled={selectionChecking}
          orgName={scheduledOccurrence?.contextLabel && scheduledOccurrence.contextLabel !== "Unassigned" ? scheduledOccurrence.contextLabel : null}
        />
      )}

      <OutOfMinutesDialog
        open={!!minutesDialog}
        onOpenChange={(next) => { if (!next) setMinutesDialog(null); }}
        usage={usage}
        user={user}
        orgPool={minutesDialog?.orgPool || null}
        saved={!!minutesDialog?.saved}
        onPlan={() => { setMinutesDialog(null); navigate("/settings"); }}
        onWriteNotes={() => { setMinutesDialog(null); openManualCompose(); }}
      />

      {aiDisclosureDialog}
    </div>
  );
};

export default HansRecording;
